import {
  type BenefitAmount,
  PlanBenefitCostType,
  PlanType
} from '~/generated/api-clients-generated'

interface IAddress {
  city: string
  state: string
  zip?: string | null
  line1: string
  line2?: string | null
}

export function formatPhone(phone: string | null | undefined) {
  if (!phone) return phone

  const digits = phone.replace(/\D/g, '').slice(-10)
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export function formatAddress(address: IAddress) {
  return formatStreet(address) + formatCityStateZip(address)
}

export function formatStreet(address: IAddress) {
  return `${address.line1}, ${address.line2 ?? ''}${address.line2 ? ', ' : ''}`
}

export function formatCityStateZip(address: IAddress) {
  return `${address.city}, ${address.state} ${address.zip?.substring(0, 5)}`
}

export function titleCase(str: string | null) {
  return str?.replace(/(^|\s)\S/g, function (t) {
    return t.toUpperCase()
  })
}

export function formatCopay(
  copay: BenefitAmount | null | undefined,
  emptyString: string = '---'
): string {
  if (_isNil(copay) || _isNil(copay.amount)) {
    return emptyString
  }

  if (copay.amountType === PlanBenefitCostType.Coinsurance) {
    return formatPct(copay.amount!)
  }

  return formatUsd(copay.amount!)
}

const dollarsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

const dollarsCentsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const percentageFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0
})

export const formatUsd = (amount: number): string => {
  return amount % 1 !== 0 ? dollarsCentsFormatter.format(amount) : dollarsFormatter.format(amount)
}

export const formatPct = (amount: number) => {
  return percentageFormatter.format(amount)
}

// Media timestamp as m:ss, or h:mm:ss past an hour. NaN/Infinity (no metadata yet) read as 0:00
export const formatMediaTime = (seconds: number): string => {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

export const stripHtml = (raw: string): string => {
  return _replace(raw, /<.+?>/g, '')
}

const namedHtmlEntities: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '\u2013',
  mdash: '\u2014',
  hellip: '\u2026',
  lsquo: '\u2018',
  rsquo: '\u2019',
  ldquo: '\u201c',
  rdquo: '\u201d'
}

const decodeHtmlEntities = (raw: string): string =>
  raw.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, ref: string) => {
    if (!ref.startsWith('#')) {
      return namedHtmlEntities[ref.toLowerCase()] ?? match
    }

    const codePoint =
      ref[1]?.toLowerCase() === 'x' ? parseInt(ref.slice(2), 16) : parseInt(ref.slice(1), 10)

    return Number.isInteger(codePoint) && codePoint > 0 && codePoint <= 0x10ffff
      ? String.fromCodePoint(codePoint)
      : match
  })

/**
 * Reduces an HTML fragment to the plain text a reader sees, for places that need the copy itself
 * rather than the markup — analytics and reporting, mainly. Tags become whitespace so text either
 * side of a `<br>` stays separated, entities are decoded, and runs of whitespace collapse to one
 * space.
 */
export const toPlainText = (raw: string | null | undefined): string => {
  if (!raw) return ''

  return decodeHtmlEntities(_replace(raw, /<.+?>/g, ' ')).replace(/\s+/g, ' ').trim()
}

export function onlyDigits(s: string | null | undefined) {
  const match = s?.match('[\\d.]+')
  return match ? +match[0] : null
}

export function normalizePhone(phone?: string | null) {
  // Assumes US format
  return phone?.replace(/\D/g, '').replace(/^1/, '1')
}

export function formatAmountData(amount: number) {
  const formatted = formatUsd(amount)
  const splitFormatted = formatted.split('.')

  return {
    dollars: splitFormatted[0].slice(1),
    cents: '.' + (splitFormatted[1] ?? '00')
  }
}

export function formatPlanType(planType: PlanType) {
  switch (planType) {
    case PlanType.MAPD:
      return 'Medicare Advantage'
    case PlanType.GAP:
      return 'Medicare Supplement'
    case PlanType.PDP:
      return 'Prescription Drugs'
  }
}

export function concatValues(
  values: string[],
  format: boolean = false,
  finalDelimiter: string = 'and'
) {
  let text = ''
  const length = values.length
  for (const [i, value] of values.entries()) {
    if (i > 0 && i != length - 1) text += ', '
    if (i > 0 && i == length - 1) text += ` ${finalDelimiter} `
    text += format ? _startCase(value?.toLowerCase()) : value
  }
  return text
}

export function isVowel(c: string) {
  return ['a', 'e', 'i', 'o', 'u'].indexOf(c.toLowerCase()) !== -1
}

export function formatBenefit(
  {
    amount,
    amountType
  }: {
    amount?: number | null
    amountType?: PlanBenefitCostType | null
  },
  withDescription?: boolean
): string {
  if (_isNil(amount)) return 'N/A'
  const str = amountType === PlanBenefitCostType.Copay ? formatUsd(amount) : formatPct(amount)

  if (!withDescription) {
    return str
  } else {
    return `${str} ${amountType === PlanBenefitCostType.Copay ? 'copay' : 'coinsurance'}`
  }
}

// Formats connecture's benefit text into structured html
export function formatBenefitsTextToHtml(input: string): string {}
